// ============================================================================
// PLATFORM HEALTH — Canonical risk/metric helper for Platform Command Center
//
// Signals → Metrics → Verdicts → Actions
//
// Every risk must be database-backed with numeric metrics.
// Zero PHI reads. Zero collection scans.
//
// INVARIANT: One tenant summary object powers ALL dashboard surfaces:
//   top cards, risk list, tenant table, config counts, value status.
//   Verdicts are never computed separately in UI components.
//
// RISK DESIGN:
//   LOW_FIRST_PASS is NOT an active risk. First-Pass Acceptance Rate and
//   Not-Yet Intercept Rate are inverse signals (they sum to 100%).
//   HIGH_NOT_YET is the canonical risk for poor Gate intake quality.
//   First-Pass Acceptance Rate is a display metric only.
// ============================================================================

import { deriveTenantConfigReadiness } from './tenantReadiness.js';
import {
  getTenants,
  getSpecialties,
  getTenantUsers,
  getPacks,
  getImpactSettings,
  getOpsErrors,
  buildDateRange,
  getDailyRollups,
  safeNum,
  sumField,
  getFlowQualityTotals,
} from './index.js';

// ============================================================================
// RISK REASON CONSTANTS (14 canonical codes)
// ============================================================================

export const RISK = {
  NO_SUBMISSIONS:           'RISK_NO_SUBMISSIONS',
  STALE_DATA:               'RISK_STALE_DATA',
  HIGH_NOT_YET:             'RISK_HIGH_NOT_YET',
  HIGH_OVERRIDE:            'RISK_HIGH_OVERRIDE',
  LOW_NOT_YET_CONVERSION:   'RISK_LOW_NOT_YET_CONVERSION',
  HIGH_REWORK_RATE:         'RISK_HIGH_REWORK_RATE',
  HIGH_REWORK_BURDEN:       'RISK_HIGH_REWORK_BURDEN',
  NO_CLOSED_LOOP:           'RISK_NO_CLOSED_LOOP',
  NO_SPECIALTIES:           'RISK_NO_SPECIALTIES',
  NO_PACKS:                 'RISK_NO_PACKS',
  NO_USERS:                 'RISK_NO_USERS',
  DEFAULT_SETTINGS:         'RISK_DEFAULT_SETTINGS',
  MISSING_SLA:              'RISK_MISSING_SLA',
  OPS_ERRORS:               'RISK_OPS_ERRORS',
  PLATFORM_VALUE_DECLINING: 'RISK_PLATFORM_VALUE_DECLINING',
  PRE_LAUNCH:               'RISK_PRE_LAUNCH',
};

export const RISK_LABELS = {
  [RISK.NO_SUBMISSIONS]:           'No gate submissions in period',
  [RISK.STALE_DATA]:               'Rollup data is stale',
  [RISK.HIGH_NOT_YET]:             'High Not-Yet intercept rate (>60%)',
  [RISK.HIGH_OVERRIDE]:            'High gate override rate (>20%)',
  [RISK.LOW_NOT_YET_CONVERSION]:   'Low Not-Yet conversion (<20%)',
  [RISK.HIGH_REWORK_RATE]:         'High rework rate (>40%)',
  [RISK.HIGH_REWORK_BURDEN]:       'High rework burden (>1.0 per referral)',
  [RISK.NO_CLOSED_LOOP]:           'No closed-loop completions',
  [RISK.NO_SPECIALTIES]:           'No specialties configured',
  [RISK.NO_PACKS]:                 'No active packs',
  [RISK.NO_USERS]:                 'No active users',
  [RISK.DEFAULT_SETTINGS]:         'Impact settings not configured',
  [RISK.MISSING_SLA]:              'SLA thresholds not set',
  [RISK.OPS_ERRORS]:               'Unresolved ops errors',
  [RISK.PLATFORM_VALUE_DECLINING]: 'Platform value declining',
  [RISK.PRE_LAUNCH]:               'Pre-launch / Setup in progress',
};

// ============================================================================
// RISK SEVERITY — single source of truth for which risks are critical vs warning
//
// All verdict functions derive from this map.
// Critical = structural/blocking problem. Warning = operational concern.
// Info = lifecycle label, not an operational risk.
//
// Config risks (NO_SPECIALTIES, NO_PACKS, NO_USERS) are critical for live
// tenants but downgraded to "setup" for pre-launch tenants at push time.
// ============================================================================

export const RISK_SEVERITY = {
  [RISK.NO_SUBMISSIONS]:           'warning',
  [RISK.STALE_DATA]:               'warning',
  [RISK.HIGH_NOT_YET]:             'warning',
  [RISK.HIGH_OVERRIDE]:            'warning',
  [RISK.LOW_NOT_YET_CONVERSION]:   'warning',
  [RISK.HIGH_REWORK_RATE]:         'warning',
  [RISK.HIGH_REWORK_BURDEN]:       'warning',
  [RISK.NO_CLOSED_LOOP]:           'warning',
  [RISK.NO_SPECIALTIES]:           'critical',
  [RISK.NO_PACKS]:                 'critical',
  [RISK.NO_USERS]:                 'critical',
  [RISK.DEFAULT_SETTINGS]:         'warning',
  [RISK.MISSING_SLA]:              'warning',
  [RISK.OPS_ERRORS]:               'warning',
  [RISK.PLATFORM_VALUE_DECLINING]: 'warning',
  [RISK.PRE_LAUNCH]:               'info',
};

// Config risk codes that downgrade to "setup" severity for pre-launch tenants
const CONFIG_RISK_CODES = new Set([
  RISK.NO_SPECIALTIES, RISK.NO_PACKS, RISK.NO_USERS,
  RISK.DEFAULT_SETTINGS, RISK.MISSING_SLA,
]);

// ============================================================================
// TENANT LIFECYCLE
// ============================================================================

const PRE_LAUNCH_STATUSES = new Set(['setup', 'onboarding', 'pending']);

function isPreLaunchTenant(lifecycle, status) {
  if (lifecycle) return lifecycle === 'pre_launch';
  return PRE_LAUNCH_STATUSES.has(status);
}

// ============================================================================
// VERDICT ENUMS
// ============================================================================

export const HEALTH_VERDICT = {
  HEALTHY: 'Healthy',
  WARNING: 'Warning',
  CRITICAL: 'Critical',
  SETUP: 'Setup',
};

export const RISK_VERDICT = { STABLE: 'Stable', WATCH: 'Watch', AT_RISK: 'At Risk' };
export const CONFIG_VERDICT = { OK: 'Config OK', WARNING: 'Config Warning', BROKEN: 'Config Broken' };
export const VALUE_VERDICT  = { INCREASING: 'Value Increasing', FLAT: 'Flat', WEAK: 'Weak', UNKNOWN: 'Unknown' };

// ============================================================================
// METRIC HELPERS (pure functions)
// ============================================================================

// First-Pass Acceptance Rate = (routine + urgent) / totalGateRuns
// DISPLAY METRIC ONLY — does not create a risk. See HIGH_NOT_YET instead.
export function firstPassAcceptanceRate(rollups) {
  const totals = getFlowQualityTotals(rollups);
  if (totals.gateSubmissions === 0) return null;
  return totals.firstPassAccepted / totals.gateSubmissions;
}

// Not-Yet Intercept Rate = notYet / gateSubmissions
// Inverse of firstPassAcceptanceRate. Used by HIGH_NOT_YET risk.
export function notYetInterceptRate(rollups) {
  const fp = firstPassAcceptanceRate(rollups);
  if (fp === null) return null;
  return 1 - fp;
}

// Gate Override Rate = allOverrides / totalGateRuns
export function gateOverrideRate(rollups) {
  const overrides = sumField(rollups, 'counts', 'overrides');
  const total = sumField(rollups, 'counts', 'totalRuns');
  if (total === 0) return null;
  return overrides / total;
}

// Not-Yet Conversion Rate = notYetConvertedCount / firstRunNotYetCount
// Numerator: engine-only conversions (excludes overrides)
// Denominator: first-run not-yet outcomes
export function notYetConversionRate(rollups) {
  const totals = getFlowQualityTotals(rollups);
  if (totals.notYetConvertedCount === null) return undefined;
  const converted = totals.notYetConvertedCount;
  const notYetTotal = sumField(rollups, 'counts', 'not_yet');
  if (notYetTotal === 0) return null;
  return converted / notYetTotal;
}

// Not-Yet Override Resolution Rate = firstRunNotYetOverrides / allFirstRunNotYet
export function notYetOverrideResolutionRate(rollups) {
  const totals = getFlowQualityTotals(rollups);
  const notYetStayed = totals.notYetCount ?? sumField(rollups, 'counts', 'not_yet');
  if (totals.notYetOverrideCount === null) return undefined;
  const overridden = totals.notYetOverrideCount ?? 0;
  if (totals.recoveredFromNotYet === null) return undefined;
  const recovered = totals.recoveredFromNotYet;
  const allFirstRunNotYet = notYetStayed + recovered + overridden;
  if (allFirstRunNotYet === 0) return null;
  return overridden / allFirstRunNotYet;
}

// Rework Rate = unique referrals with rework / referrals entered ops
// Referral-level rate: each referral counted once regardless of request count.
export function reworkRate(rollups) {
  const enteredOps = getFlowQualityTotals(rollups).enteredOps || sumField(rollups, 'flow', 'created');
  if (enteredOps < 10) return null;
  const referralsWithRework = sumField(rollups, 'rework', 'referralsWithRework');
  return referralsWithRework / enteredOps;
}

// Rework Burden = total rework requests created / referrals entered ops
// Density metric: one referral can produce multiple requests.
export function reworkBurden(rollups) {
  const enteredOps = getFlowQualityTotals(rollups).enteredOps || sumField(rollups, 'flow', 'created');
  if (enteredOps < 10) return null;
  const reworkCreated = sumField(rollups, 'rework', 'requestsCreated');
  return reworkCreated / enteredOps;
}

// Closed Loop Rate = closedLoop / seen
// Only meaningful when patients have been seen.
export function closedLoopRate(rollups) {
  const seen = sumField(rollups, 'flow', 'seen');
  if (seen === 0) return null;
  const closed = sumField(rollups, 'flow', 'closedLoop');
  return closed / seen;
}

export function totalMinutesReclaimed(rollups) {
  return sumField(rollups, 'value', 'reclaimedMinutes');
}

export function totalGateSubmissions(rollups) {
  return getFlowQualityTotals(rollups).gateSubmissions;
}

// ============================================================================
// ROLLUP FRESHNESS
// ============================================================================

function rollupFreshness(rollups) {
  const now = Date.now();
  const validDates = rollups
    .filter(r => r.dateId)
    .map(r => new Date(r.dateId + 'T00:00:00Z').getTime())
    .filter(t => !isNaN(t));

  if (validDates.length === 0) return 'missing';

  const mostRecent = Math.max(...validDates);
  const hoursAgo = (now - mostRecent) / (1000 * 60 * 60);

  const hasRollupDoc = rollups.some(r =>
    r.generatedAt || r.updatedAt || safeNum(r, 'counts', 'totalRuns') > 0
  );

  if (!hasRollupDoc) return 'missing';
  if (hoursAgo > 48) return 'stale';
  return 'fresh';
}

// ============================================================================
// COMPUTE TENANT SUMMARY — the core function
//
// This is the SINGLE object that powers every dashboard surface.
// ============================================================================

export function computeTenantSummary({
  tenantId,
  tenantName,
  tenantStatus,
  tenantLifecycle,
  rollups,
  prevRollups,
  specialtyCount,
  packCount,
  userCount,
  impactSettings,
  opsErrors,
}) {
  const risks = [];
  const freshness = rollupFreshness(rollups);
  const gateSubmissions = totalGateSubmissions(rollups);
  const minutesReclaimed = totalMinutesReclaimed(rollups);
  const prevMinutesReclaimed = prevRollups ? totalMinutesReclaimed(prevRollups) : null;
  const preLaunch = isPreLaunchTenant(tenantLifecycle, tenantStatus);
  const isDefaultSettings = !impactSettings || impactSettings._isDefault;

  // --- Pre-launch lifecycle label (info, not an operational risk) ---
  if (preLaunch) {
    risks.push({ code: RISK.PRE_LAUNCH, severity: 'info', metric: 0 });
  }

  // --- Activity signals (live tenants only) ---
  if (!preLaunch) {
    if (gateSubmissions === 0) {
      risks.push({ code: RISK.NO_SUBMISSIONS, severity: 'warning', metric: 0 });
    }
    if (freshness === 'stale') {
      risks.push({ code: RISK.STALE_DATA, severity: 'warning', metric: 0 });
    }
  }

  // --- Compute all metrics (always, for display) ---
  const fpRate = firstPassAcceptanceRate(rollups);
  const nyIntercept = notYetInterceptRate(rollups);
  const oRate = gateOverrideRate(rollups);
  const nyConv = notYetConversionRate(rollups);
  const nyOverrideRes = notYetOverrideResolutionRate(rollups);
  const rwRate = reworkRate(rollups);
  const rwBurden = reworkBurden(rollups);
  const clRate = closedLoopRate(rollups);

  // First-run Not-Yet count for LOW_NOT_YET_CONVERSION guard
  const firstRunNotYetCount = sumField(rollups, 'counts', 'not_yet');

  // --- Operational risk signals (live tenants only) ---
  if (!preLaunch) {
    // HIGH_NOT_YET: >60% intercept rate, minimum 10 submissions
    if (nyIntercept !== null && nyIntercept > 0.6 && gateSubmissions >= 10) {
      risks.push({ code: RISK.HIGH_NOT_YET, severity: 'warning', metric: Math.round(nyIntercept * 100) });
    }

    // HIGH_OVERRIDE: >20% override rate, minimum 10 submissions
    if (oRate !== null && oRate > 0.2 && gateSubmissions >= 10) {
      risks.push({ code: RISK.HIGH_OVERRIDE, severity: 'warning', metric: Math.round(oRate * 100) });
    }

    // LOW_NOT_YET_CONVERSION: <20% conversion, minimum 5 first-run Not-Yet
    if (typeof nyConv === 'number' && nyConv < 0.2 && firstRunNotYetCount >= 5) {
      risks.push({ code: RISK.LOW_NOT_YET_CONVERSION, severity: 'warning', metric: Math.round(nyConv * 100) });
    }

    // HIGH_REWORK_RATE: >40% of referrals entering ops have rework
    if (rwRate !== null && rwRate > 0.4) {
      risks.push({ code: RISK.HIGH_REWORK_RATE, severity: 'warning', metric: Math.round(rwRate * 100) });
    }

    // HIGH_REWORK_BURDEN: >1.0 rework requests per referral entering ops
    if (rwBurden !== null && rwBurden > 1.0) {
      risks.push({ code: RISK.HIGH_REWORK_BURDEN, severity: 'warning', metric: Math.round(rwBurden * 100) / 100 });
    }

    // NO_CLOSED_LOOP: patients seen but none closed-loop documented
    const seenCount = sumField(rollups, 'flow', 'seen');
    const closedLoopCount = sumField(rollups, 'flow', 'closedLoop');
    if (seenCount > 0 && closedLoopCount === 0) {
      risks.push({ code: RISK.NO_CLOSED_LOOP, severity: 'warning', metric: 0 });
    }
  }

  // --- Configuration signals (always show) ---
  // For pre-launch tenants these are "setup issues", not operational failures.
  // The severity field is downgraded to "setup" for pre-launch tenants.
  const configSeverity = preLaunch ? 'setup' : 'critical';
  const settingSeverity = preLaunch ? 'setup' : 'warning';

  if (specialtyCount === 0) {
    risks.push({ code: RISK.NO_SPECIALTIES, severity: configSeverity, metric: 0 });
  }
  if (packCount === 0) {
    risks.push({ code: RISK.NO_PACKS, severity: configSeverity, metric: 0 });
  }
  if (userCount === 0) {
    risks.push({ code: RISK.NO_USERS, severity: configSeverity, metric: 0 });
  }
  if (isDefaultSettings) {
    risks.push({ code: RISK.DEFAULT_SETTINGS, severity: settingSeverity, metric: 0 });
  }

  const hasSla = impactSettings?.sla?.states && Object.keys(impactSettings.sla.states).length > 0;
  if (!hasSla && !isDefaultSettings) {
    risks.push({ code: RISK.MISSING_SLA, severity: settingSeverity, metric: 0 });
  }

  // --- Error signals ---
  const unresolvedErrors = (opsErrors || []).filter(e => !e.resolved);
  if (unresolvedErrors.length > 0) {
    const hasCriticalError = unresolvedErrors.some(e => e.severity === 'critical');
    risks.push({
      code: RISK.OPS_ERRORS,
      severity: hasCriticalError ? 'critical' : 'warning',
      metric: unresolvedErrors.length,
    });
  }

  // --- Value signals (live tenants only) ---
  if (!preLaunch && prevMinutesReclaimed !== null && prevMinutesReclaimed > 0 && minutesReclaimed < prevMinutesReclaimed * 0.8) {
    risks.push({ code: RISK.PLATFORM_VALUE_DECLINING, severity: 'warning', metric: Math.round(minutesReclaimed) });
  }

  // --- Compute verdicts from the SAME risks array ---
  const healthVerdict = computeHealthVerdict(risks, preLaunch, gateSubmissions);
  const riskVerdict = computeRiskVerdict(risks);
  const configVerdict = computeConfigVerdict(risks);
  const prevGateSubmissions = prevRollups ? totalGateSubmissions(prevRollups) : null;
  const valueResult = computeValueVerdict({
    minutesReclaimed,
    prevMinutesReclaimed,
    gateSubmissions,
    prevGateSubmissions,
    isDefaultSettings,
    freshness,
    clRate,
    rwRate,
    unresolvedErrorCount: unresolvedErrors.length,
  });

  return {
    tenantId,
    tenantName,
    tenantStatus,
    freshness,
    preLaunch,
    risks,
    verdicts: {
      health: healthVerdict,
      risk: riskVerdict,
      config: configVerdict,
      value: valueResult.verdict,
    },
    valueReason: valueResult.reason,
    metrics: {
      gateSubmissions,
      minutesReclaimed,
      prevMinutesReclaimed,
      firstPassAcceptanceRate: fpRate,
      notYetInterceptRate: nyIntercept,
      gateOverrideRate: oRate,
      notYetConversionRate: nyConv,
      notYetOverrideResolutionRate: nyOverrideRes,
      reworkRate: rwRate,
      reworkBurden: rwBurden,
      closedLoopRate: clRate,
      specialtyCount,
      packCount,
      userCount,
      unresolvedErrorCount: unresolvedErrors.length,
    },
  };
}

// ============================================================================
// VERDICT LOGIC — all derived from the same risks array
//
// Severity is now per-risk (carried in the risk object), not just from
// the static RISK_SEVERITY map. This allows config risks to downgrade
// to "setup" for pre-launch tenants while keeping the map as the default.
// ============================================================================

function resolvedSeverity(risk) {
  return risk.severity || RISK_SEVERITY[risk.code] || 'warning';
}

function hasCriticalRisk(risks) {
  return risks.some(r => resolvedSeverity(r) === 'critical');
}

function computeHealthVerdict(risks, preLaunch, gateSubmissions) {
  if (preLaunch && gateSubmissions === 0) return HEALTH_VERDICT.SETUP;
  if (hasCriticalRisk(risks)) return HEALTH_VERDICT.CRITICAL;
  const actionableRisks = risks.filter(r => resolvedSeverity(r) !== 'info' && resolvedSeverity(r) !== 'setup');
  if (actionableRisks.length > 0) return HEALTH_VERDICT.WARNING;
  return HEALTH_VERDICT.HEALTHY;
}

function computeRiskVerdict(risks) {
  const actionableRisks = risks.filter(r => resolvedSeverity(r) !== 'info' && resolvedSeverity(r) !== 'setup');
  if (actionableRisks.length === 0) return RISK_VERDICT.STABLE;
  if (hasCriticalRisk(risks)) return RISK_VERDICT.AT_RISK;
  return RISK_VERDICT.WATCH;
}

function computeConfigVerdict(risks) {
  const configRisks = risks.filter(r => CONFIG_RISK_CODES.has(r.code));
  const broken = configRisks.filter(r =>
    [RISK.NO_SPECIALTIES, RISK.NO_PACKS, RISK.NO_USERS].includes(r.code)
    && resolvedSeverity(r) === 'critical'
  );
  if (broken.length > 0) return CONFIG_VERDICT.BROKEN;
  if (configRisks.length > 0) return CONFIG_VERDICT.WARNING;
  return CONFIG_VERDICT.OK;
}

function computeValueVerdict({
  minutesReclaimed,
  prevMinutesReclaimed,
  gateSubmissions,
  prevGateSubmissions,
  isDefaultSettings,
  freshness,
  clRate,
  rwRate,
  unresolvedErrorCount,
}) {
  if (freshness === 'missing') {
    return { verdict: VALUE_VERDICT.UNKNOWN, reason: 'No rollup data available' };
  }
  if (gateSubmissions === 0) {
    return { verdict: VALUE_VERDICT.UNKNOWN, reason: 'No submissions in period' };
  }
  if (isDefaultSettings && minutesReclaimed === 0) {
    return { verdict: VALUE_VERDICT.UNKNOWN, reason: 'Impact settings not configured' };
  }

  const activityHealthy = gateSubmissions > 0;
  const activityGrowing = prevGateSubmissions !== null && prevGateSubmissions > 0
    ? gateSubmissions >= prevGateSubmissions * 0.9
    : true;
  const closedLoopHealthy = clRate !== null && clRate > 0;
  const reworkLow = rwRate === null || rwRate <= 0.4;
  const noLiveFires = unresolvedErrorCount === 0;

  const weakSignals = [];
  if (!activityGrowing) weakSignals.push('Referral activity declining');
  if (clRate !== null && clRate === 0) weakSignals.push('No closed-loop completions');
  if (rwRate !== null && rwRate > 0.4) weakSignals.push('High rework rate');
  if (!noLiveFires) weakSignals.push('Unresolved ops errors');

  if (activityHealthy && activityGrowing && closedLoopHealthy && reworkLow && noLiveFires) {
    return { verdict: VALUE_VERDICT.INCREASING, reason: null };
  }

  if (weakSignals.length >= 3) {
    return { verdict: VALUE_VERDICT.WEAK, reason: weakSignals[0] };
  }

  if (prevMinutesReclaimed !== null && prevMinutesReclaimed > 0) {
    const ratio = minutesReclaimed / prevMinutesReclaimed;
    if (ratio >= 1.1) return { verdict: VALUE_VERDICT.INCREASING, reason: null };
    if (ratio >= 0.9) return { verdict: VALUE_VERDICT.FLAT, reason: weakSignals[0] || null };
    if (activityHealthy && closedLoopHealthy && reworkLow) {
      return { verdict: VALUE_VERDICT.FLAT, reason: 'Minutes declined but workflow health is strong' };
    }
    return { verdict: VALUE_VERDICT.WEAK, reason: weakSignals[0] || 'Platform value declining' };
  }

  if (minutesReclaimed > 0) return { verdict: VALUE_VERDICT.INCREASING, reason: null };
  return { verdict: VALUE_VERDICT.UNKNOWN, reason: 'Insufficient comparison data' };
}

// ============================================================================
// PLATFORM-LEVEL AGGREGATION
// ============================================================================

export function aggregateVerdicts(summaries) {
  const counts = {
    health: {
      [HEALTH_VERDICT.HEALTHY]: 0,
      [HEALTH_VERDICT.WARNING]: 0,
      [HEALTH_VERDICT.CRITICAL]: 0,
      [HEALTH_VERDICT.SETUP]: 0,
    },
    risk:   { [RISK_VERDICT.STABLE]: 0, [RISK_VERDICT.WATCH]: 0, [RISK_VERDICT.AT_RISK]: 0 },
    config: { [CONFIG_VERDICT.OK]: 0, [CONFIG_VERDICT.WARNING]: 0, [CONFIG_VERDICT.BROKEN]: 0 },
    value:  { [VALUE_VERDICT.INCREASING]: 0, [VALUE_VERDICT.FLAT]: 0, [VALUE_VERDICT.WEAK]: 0, [VALUE_VERDICT.UNKNOWN]: 0 },
  };

  for (const s of summaries) {
    counts.health[s.verdicts.health]++;
    counts.risk[s.verdicts.risk]++;
    counts.config[s.verdicts.config]++;
    counts.value[s.verdicts.value]++;
  }

  const totalMinutes = summaries.reduce((a, s) => a + s.metrics.minutesReclaimed, 0);
  const totalSubmissions = summaries.reduce((a, s) => a + s.metrics.gateSubmissions, 0);
  const totalErrors = summaries.reduce((a, s) => a + s.metrics.unresolvedErrorCount, 0);

  return { counts, totalMinutes, totalSubmissions, totalErrors, tenantCount: summaries.length };
}

// ============================================================================
// DATA LOADER — fetches all tenant data in parallel, returns summaries
// ============================================================================

export async function loadPlatformHealth(nDays = 7) {
  const tenantsMap = await getTenants();
  const tenantIds = Object.keys(tenantsMap);

  if (tenantIds.length === 0) {
    return { summaries: [], aggregate: aggregateVerdicts([]) };
  }

  const dateIds = buildDateRange(nDays);
  const prevDateIds = buildDateRange(nDays).map(d => {
    const dt = new Date(d + 'T00:00:00Z');
    dt.setUTCDate(dt.getUTCDate() - nDays);
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dt.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });

  const summaries = await Promise.all(
    tenantIds.map(async (tenantId) => {
      const tenantData = tenantsMap[tenantId];
      const tenantName = tenantData?.name || tenantId;
      const tenantStatus = tenantData?.status || 'unknown';
      const tenantLifecycle = tenantData?.lifecycle || null;

      const [rollups, prevRollups, specs, users, impactSettings, opsErrors] = await Promise.all([
        getDailyRollups(tenantId, dateIds).catch(() => []),
        getDailyRollups(tenantId, prevDateIds).catch(() => []),
        getSpecialties(tenantId).catch(() => ({})),
        getTenantUsers(tenantId).catch(() => []),
        getImpactSettings(tenantId).catch(() => ({ _isDefault: true })),
        getOpsErrors(tenantId, { resolved: false, daysBack: nDays, limit: 50 }).catch(() => []),
      ]);

      let packCount = 0;
      const specIds = Object.keys(specs);
      if (specIds.length > 0) {
        const packResults = await Promise.all(
          specIds.map(specId => getPacks(tenantId, specId).catch(() => ({})))
        );
        packCount = packResults.reduce((acc, packs) => {
          const packList = Object.values(packs);
          return acc + packList.filter(p => p.status === 'active').length;
        }, 0);
      }

      return computeTenantSummary({
        tenantId,
        tenantName,
        tenantStatus,
        tenantLifecycle,
        rollups,
        prevRollups,
        specialtyCount: specIds.length,
        packCount,
        userCount: Array.isArray(users) ? users.filter(u => u.status === 'active').length : 0,
        impactSettings,
        opsErrors: Array.isArray(opsErrors) ? opsErrors : [],
      });
    })
  );

  return {
    summaries,
    aggregate: aggregateVerdicts(summaries),
  };
}

// ============================================================================
// TENANT CONFIG READINESS — lightweight, READ-ONLY (no rollups / metrics / ops).
// ----------------------------------------------------------------------------
// Used by the onboarding "Mark Live" gate. Reads only NEVER-PHI config data for
// ONE tenant: enabled specialties, active members, active packs (+ impact
// settings, advisory only). Never reads PHI. Never writes. Fails safe:
// if readiness cannot be determined, canGoLive is false (with readinessError).
// ============================================================================

export async function loadTenantConfigReadiness(tenantId) {
  try {
    const [specs, users, impactSettings] = await Promise.all([
      getSpecialties(tenantId).catch(() => ({})),
      getTenantUsers(tenantId).catch(() => []),
      getImpactSettings(tenantId).catch(() => ({ _isDefault: true })),
    ]);

    const specIds = Object.keys(specs || {});
    const enabledSpecialtyCount = specIds.length;

    let activePackCount = 0;
    if (specIds.length > 0) {
      const packResults = await Promise.all(
        specIds.map((specId) => getPacks(tenantId, specId).catch(() => ({}))),
      );
      activePackCount = packResults.reduce(
        (acc, packs) => acc + Object.values(packs).filter((p) => p.status === 'active').length,
        0,
      );
    }

    const activeUserCount = Array.isArray(users)
      ? users.filter((u) => u.status === 'active').length
      : 0;
    const isDefaultSettings = impactSettings?._isDefault === true;
    const hasSla = !!(impactSettings?.sla?.states && Object.keys(impactSettings.sla.states).length > 0);

    return deriveTenantConfigReadiness({
      enabledSpecialtyCount,
      activeUserCount,
      activePackCount,
      isDefaultSettings,
      hasSla,
    });
  } catch (err) {
    // Fail safe — readiness could not be verified, so the tenant cannot go Live.
    return {
      enabledSpecialtyCount: 0,
      activeUserCount: 0,
      activePackCount: 0,
      criticalMissing: [],
      advisory: [],
      canGoLive: false,
      readinessError: err?.message || 'Could not verify readiness',
    };
  }
}
